import { Component, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { TransactionService } from 'src/app/services/transaction.service';
import { TransactionStatus } from 'src/app/shared/models/transactions/transaction-status';
import {
  AbstractControl,
  AbstractControlOptions,
  Form,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NewTransaction } from 'src/app/shared/models/transactions/new-transaction';
import { HttpErrorResponse } from '@angular/common/http';
import { UserBilling } from 'src/app/shared/models/users/user-billing';
import { UserService } from 'src/app/services/user.service';
import { UserFiaPins } from 'src/app/shared/models/users/user-fia-pins';
import { ModalDirective } from 'angular-bootstrap-md';
import { UserSearchResult } from 'src/app/shared/models/users/user-search-result';
import { LookupService } from 'src/app/services/lookup.service';
import { SavedTradesResponse } from 'src/app/shared/models/transactions/saved-trades-response';

@Component({
  selector: 'app-add-new-trade',
  templateUrl: './add-new-trade.component.html',
  styleUrls: ['./add-new-trade.component.scss'],
})
export class AddNewTradeComponent implements OnChanges {
  @ViewChild('createNewTradeModal', { static: true }) createNewTradeModal: ModalDirective;
  //TODO: TradeIdentifierPackage - could pass in a whole object with these id values below
  //@Input() clientId: number = 296; // Jarron
  @Input() adminUserId: number = 296; // Jarron
  @Input() user: UserSearchResult;
  @Input() showModal: boolean = false;

  @Output() showModalChange = new EventEmitter<boolean>();

  ArbFacilitatorId: number = 1; //Dooya
  TradeAmountCurrencyId: number = 95;
  BrokerId: number = 1; //Phynans

  newTradeForm: FormGroup;
  newTransaction: NewTransaction = new NewTransaction();
  statusList: { value: number; label: string }[] = [
    {
      value: 100,
      label: 'Posted to Dooya',
    },
  ];

  combinedTransactionDateTime: string = '';
  tradeAmount: number = 0;
  fiaAmount: number = 0;

  usablePins: UserFiaPins[] = [];
  pinsToUse: UserFiaPins[] = [];
  usablePinsFormArray: FormArray = new FormArray([]);

  billingTypes = UserBilling.userBillingTypes;

  //Error handling
  errorResponse: HttpErrorResponse;
  showError: boolean = false;

  //Form state
  loading = false;
  success = false;

  constructor(
    private formBuilder: FormBuilder,
    private transactionService: TransactionService,
    private userService: UserService,
    private lookupService: LookupService
  ) {}

  populateUserFiaPins() {
    this.usablePins = [];
    if (!this.user) {
      return;
    }
    this.userService.GetUsersFiaPins(this.user.id, true).subscribe({
      next: (pins: UserFiaPins[]) => {
        this.usablePins = pins;
        this.populateUsablePinsFormArray(this.usablePins);
      },
      error: (error) => {
        this.showError = true;
        this.errorResponse = error;
      },
    });
  }

  private populateUsablePinsFormArray(pins: UserFiaPins[]) {
    this.usablePinsFormArray.clear();
    pins.forEach((x) => {
      this.usablePinsFormArray.push(
        new FormGroup({
          pin: new FormControl(x.pin),
          pinExpiryDate: new FormControl(x.pinExpiryDate),
          pinBalance: new FormControl(x.pinBalance),
          pinAmount: new FormControl(x.pinAmount),
          transactionUsedAmount: new FormControl(null),
        })
      );
    });
  }
  atLeastOneNumberValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const sdaAmountControl = this.sdaAmount; // Access sdaAmount control
      // Check if sdaAmount is empty or null
      const sdaAmountIsEmpty = !sdaAmountControl?.value;

      // Check if at least one number field in formArray has a value
      let total = 0;
      this.usablePinsFormArray.controls.forEach((ctrl) => {
        const numberValue: number = ctrl.get('transactionUsedAmount')?.value;
        if (!numberValue) {
          return;
        }
        total += numberValue;
      });

      const hasNumberInFormArray = total > 0;

      // Validate based on the conditions
      if (sdaAmountIsEmpty && !hasNumberInFormArray) {
        return { atLeastOneNumber: true }; // Both fields are empty
      } else {
        return null; // At least one field has a value
      }
    };
  }

  generateId(i: number) {
    return `transactionUsedAmount_${i}`;
  }

  ngOnChanges(): void {
    this.showHideModal(null);
    this.populateUserFiaPins();

    this.newTradeForm = this.formBuilder.group({
      sdaAmount: '',
      transactionStatus: [100, [Validators.required]],
      pipsAmount: 660,
      transactionDate: '',
      timeHrs: '',
      timeMins: '',
      billingType: [2, [Validators.required]],
      pins: this.usablePinsFormArray,
    });

    this.newTradeForm.addValidators(this.atLeastOneNumberValidator());
  }

  isInvalidInput(control: AbstractControl) {
    return control.touched && control.invalid;
  }

  async submitHandler() {
    if (this.newTradeForm.valid) {
      // Form is valid, submit it
      console.log(this.newTradeForm.value);
    } else {
      // Form is invalid, handle errors
      console.log('Form is invalid');
      return;
    }

    this.loading = true;
    this.createNewTransaction();
    //create new transaction object from form values
    const transactionList: NewTransaction[] = [];

    transactionList.push(this.newTransaction);

    //submit

    this.transactionService.NewTransactions(transactionList).subscribe({
      next: (response: SavedTradesResponse) => {
        this.success = true;
      },
      error: (error) => {
        this.showError = true;
        this.errorResponse = error;
      },
    });
  }

  createPinsToUseListAndFiaAmountTotal(form) {
    this.pinsToUse = [];
    this.fiaAmount = 0;

    form.pins?.forEach((formPin) => {
      if (formPin.transactionUsedAmount > 0) {
        const foundIndex = this.usablePins.findIndex((x) => x.pin === formPin.pin);

        let usedPin = this.usablePins[foundIndex];
        usedPin.transactionUsedAmount = formPin.transactionUsedAmount;
        this.fiaAmount += formPin.transactionUsedAmount;

        this.pinsToUse.push(usedPin);
      }
    });
    this.calculateTradeAmount();
  }

  createNewTransaction() {
    this.newTransaction.userId = this.user.id;
    this.newTransaction.arbFacilitatorId = this.ArbFacilitatorId;
    this.newTransaction.brokerId = this.BrokerId;
    this.newTransaction.tradeAmountCurrencyId = this.TradeAmountCurrencyId;
    this.newTransaction.createdByUserId = this.adminUserId;
    this.newTransaction.fiaAmount = this.fiaAmount;
    this.newTransaction.sdaAmount = this.sdaAmount.value;
    this.newTransaction.billingType = this.billingType.value;

    this.newTransaction.tradeAmount = this.tradeAmount;
    this.newTransaction.transactionStartDate = this.buildDateString();
    this.newTransaction.userFiaPins = this.pinsToUse;

    this.newTransaction.pips = this.pipsAmount.value;
    this.newTransaction.overridePips = !!this.pipsAmount.value;
  }

  calculateTradeAmount() {
    this.tradeAmount = +this.fiaAmount + +this.sdaAmount.value;
  }

  buildDateString(): string {
    let date = this.newTradeForm.value.transactionDate;
    if (!date) {
      return null;
    }
    let hrs = this.padTime(this.newTradeForm.value.timeHrs);
    let mins = this.padTime(this.newTradeForm.value.timeMins);

    return `${date} ${hrs}:${mins}`;
  }

  //addUsedPins(i: number) {}

  private padTime(value: number) {
    let valueString = '';
    if (value < 10) {
      valueString = `0${value}`;
    } else {
      valueString = `${value}`;
    }
    return valueString;
  }

  GetBillingTypeName(id: number) {
    if (!id) {
      return;
    }
    return UserBilling.GetDisplayNameById(id);
  }
  GetTransactionStatusName(id: number) {
    if (!id) {
      return;
    }

    let status = this.FindTransactionStatusInListById(id);
    return status.name;
  }

  FindTransactionStatusInListById(statusId: number) {
    const foundStatus = this.statusList.find((x) => x.value === statusId);

    let status = new TransactionStatus();
    status.id = statusId;
    status.name = foundStatus.label;

    if (!status) {
      status.name = 'Unknown Status';
    }

    return status;
  }

  onClosed(event: any) {
    if (this.showModal != false) {
      this.showModal = false;
      this.showModalChange.emit(this.showModal);
    }
  }

  showHideModal(toggle: boolean) {
    if (toggle != null) {
      this.showModal = toggle;
    }

    if (this.showModal) {
      this.createNewTradeModal.show();
      // this.userProfile = <UserProfile>JSON.parse(JSON.stringify(this.selectedUserProfile));
      // this.GetTransactionData();
    } else if (!this.showModal) {
      this.showError = false;
      this.errorResponse = null;
      this.createNewTradeModal.hide();
    }

    this.showModalChange.emit(this.showModal);
  }

  //Basic form getters
  // get fiaAmount() {
  //   return this.newTradeForm.get('fiaAmount');
  // }
  get sdaAmount() {
    return this.newTradeForm.get('sdaAmount');
  }
  // get totalTradeAmount() {
  //   return this.newTradeForm.get('totalTradeAmount');
  // }
  get bankFees() {
    return this.newTradeForm.get('bankFees');
  }
  get billingType() {
    return this.newTradeForm.get('billingType');
  }
  get transactionStatus() {
    return this.newTradeForm.get('transactionStatus');
  }
  get pipsAmount() {
    return this.newTradeForm.get('pipsAmount');
  }
  get transactionDate() {
    return this.newTradeForm.get('transactionDate');
  }
  get timeHrs() {
    return this.newTradeForm.get('timeHrs');
  }
  get timeMins() {
    return this.newTradeForm.get('timeMins');
  }
  get pins() {
    return <FormArray>this.newTradeForm.get('pins');
  }
}
